import { FileOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Dropdown, Flex, Skeleton, Table, Tag, type TableProps, Typography } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React from "react"
import { useTranslation } from "react-i18next"

import { getFormatDate } from "../../../../../../../utils/date/get-date-safe.ts"
import type { AlbumListItem } from "../../../../../../Album/types/album.types.ts"
import { albumsScrollContainer } from "../albums-scroll-container.ts"

const { Text } = Typography

export const AlbumsListLoader = ({
  count = 4,
  className = ""
}: {
  count?: number
  className?: string
}) => (
  <Flex vertical gap="small" className={className}>
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton.Node key={i} active className="w-full !h-10" />
    ))}
  </Flex>
)

interface AlbumsListViewProps {
  albums: AlbumListItem[]
  createAllowed: boolean
  onCreateAlbumClick?: () => void
  onView: (id: string) => void
  menuItemsFor: (album: AlbumListItem) => MenuItemType[]
}

export function AlbumsListView({
  albums,
  createAllowed,
  onCreateAlbumClick,
  onView,
  menuItemsFor
}: AlbumsListViewProps) {
  const { t } = useTranslation()

  const columns: TableProps<AlbumListItem>["columns"] = [
    {
      title: t("files.albums.columns.album"),
      key: "album",
      align: "left",
      render: (_, album) => {
        const cover = album.attachments?.find((a) => a.mime_type?.startsWith("image/"))
        return (
          <Flex align="center" gap="small">
            <div className="size-8 rounded overflow-hidden bg-gray-50 flex items-center justify-center shrink-0">
              {cover ? (
                <img
                  src={cover.file}
                  alt=""
                  className="size-full object-cover"
                  loading="lazy"
                />
              ) : (
                <FileOutlined className="text-gray-300" />
              )}
            </div>
            <a onClick={() => onView(album.id)}>
              <Text ellipsis={{ tooltip: album.name }}>{album.name}</Text>
            </a>
            {album.created_by_is_customer && (
              <Tag color="blue" className="!m-0 !text-xs shrink-0">
                {album.created_by_name
                  ? t("files.filters.sharedByCustomerNamed", {
                      name: album.created_by_name
                    })
                  : t("files.filters.sharedByCustomer")}
              </Tag>
            )}
          </Flex>
        )
      }
    },
    {
      title: t("files.albums.columns.files"),
      key: "files",
      render: (_, album) => album.attachments_count || 0
    },
    {
      title: t("files.albums.columns.modified"),
      key: "modified",
      render: (_, album) => getFormatDate(album.updated) ?? album.created
    },
    {
      title: t("files.albums.columns.actions"),
      key: "actions",
      width: 80,
      render: (_, album) => {
        const items = menuItemsFor(album)
        if (!items.length) return null
        return (
          <Dropdown trigger={["click"]} menu={{ items }}>
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              aria-label={t("files.albums.actions.menu", { name: album.name })}
            />
          </Dropdown>
        )
      }
    }
  ]

  return (
    <Flex vertical gap="small">
      {createAllowed && (
        <div
          onClick={onCreateAlbumClick}
          className="flex items-center gap-3 rounded-md border border-dashed border-border px-3 py-2 cursor-pointer hover:border-primary"
        >
          <PlusOutlined className="text-primary" />
          <Text strong>{t("files.albums.createCard.title")}</Text>
          <Text type="secondary" ellipsis className="text-sm">
            {t("files.albums.createCard.description")}
          </Text>
        </div>
      )}

      <Table<AlbumListItem>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={albums}
        pagination={false}
        scroll={{ x: "max-content" }}
        sticky={{ getContainer: albumsScrollContainer }}
      />
    </Flex>
  )
}
